import { Controller } from "@hotwired/stimulus"
import { vapidApplicationServerKey } from "../utils/push_vapid"

// Manages Web Push enrollment for the current device from the settings page:
// registers the service worker, requests notification permission, subscribes
// with the site's VAPID key and syncs the subscription with the server.
export default class extends Controller {
  static targets = ["status", "badge", "enableButton", "disableButton", "testButton", "testResult"]

  async connect() {
    if (!this.supported()) {
      this.showState("unsupported")
      return
    }

    try {
      this.registration = await navigator.serviceWorker.register("/service_worker.js")
    } catch (error) {
      this.showState("error")
      return
    }

    if (Notification.permission === "denied") {
      this.showState("denied")
      return
    }

    let subscription = await this.registration.pushManager.getSubscription()
    this.showState(subscription ? "subscribed" : "ready")
  }

  async subscribe() {
    let permission = await Notification.requestPermission()
    if (permission !== "granted") {
      this.showState("denied")
      return
    }

    try {
      let subscription = await this.registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: vapidApplicationServerKey(this.vapidKey())
      })
      let json = subscription.toJSON()
      let response = await fetch("/push_subscription", {
        method: "POST",
        headers: this.headers(),
        body: JSON.stringify({ subscription: { endpoint: json.endpoint, p256dh: json.keys.p256dh, auth: json.keys.auth } })
      })
      if (!response.ok) throw new Error(`server responded ${response.status}`)
      this.showState("subscribed")
    } catch (error) {
      this.showState("error")
    }
  }

  async unsubscribe() {
    let subscription = await this.registration.pushManager.getSubscription()
    if (subscription) {
      await fetch(`/push_subscription?endpoint=${encodeURIComponent(subscription.endpoint)}`, { method: "DELETE", headers: this.headers() })
      await subscription.unsubscribe()
    }
    this.showState("ready")
  }

  async sendTest() {
    if (!this.hasTestResultTarget) return
    if (this.hasTestButtonTarget) this.testButtonTarget.disabled = true
    this.showTestResult("Sending…")

    try {
      let response = await fetch("/push_subscription/test", { method: "POST", headers: this.headers() })
      let data = await response.json().catch(() => ({}))
      if (data.ok) {
        if (data.sent > 0) {
          this.showTestResult(`Sent to ${data.sent} device${data.sent === 1 ? "" : "s"} — check your notifications. If nothing appeared, check your system's notification settings or Focus mode.`)
        } else {
          this.showTestResult("No devices enrolled — enable notifications first.")
        }
      } else if (data.reason === "unconfigured") {
        this.showTestResult("Push isn't configured on this server.")
      } else {
        this.showTestResult("Couldn't send a test notification. Please try again.")
      }
    } catch (error) {
      this.showTestResult("Couldn't send a test notification. Please try again.")
    } finally {
      if (this.hasTestButtonTarget) this.testButtonTarget.disabled = false
    }
  }

  showTestResult(message) {
    if (!this.hasTestResultTarget) return
    this.testResultTarget.textContent = message
    this.testResultTarget.hidden = false
  }

  supported() {
    return "serviceWorker" in navigator && "PushManager" in window && "Notification" in window
  }

  vapidKey() {
    let meta = document.querySelector("meta[name='vapid-public-key']")
    return meta ? meta.content : null
  }

  headers() {
    let token = document.querySelector("meta[name='csrf-token']")
    return { "Content-Type": "application/json", "X-CSRF-Token": token ? token.content : "" }
  }

  showState(state) {
    let messages = {
      unsupported: "Not available in this browser. On iPhone/iPad, open Jane in Safari and add it to your Home Screen first, then enable notifications from the installed app.",
      denied: "Notifications are blocked for this site. Allow them in your browser settings, then reload this page.",
      ready: "Get notified when the assistant replies or an email needs your attention.",
      subscribed: "This device will be notified when the assistant replies or an email needs your attention.",
      error: "Something went wrong setting up notifications. Please try again."
    }
    if (this.hasStatusTarget) this.statusTarget.textContent = messages[state] || ""
    if (this.hasBadgeTarget) {
      let on = state === "subscribed"
      this.badgeTarget.textContent = on ? "On" : "Off"
      this.badgeTarget.classList.toggle("bg-success", on)
      this.badgeTarget.classList.toggle("bg-secondary", !on)
    }
    if (this.hasEnableButtonTarget) this.enableButtonTarget.hidden = state !== "ready" && state !== "error"
    if (this.hasDisableButtonTarget) this.disableButtonTarget.hidden = state !== "subscribed"
  }
}
